import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "src/components/ui/Button";
import Preloader from "src/components/ui/Preloader";

const PAYMENT_ELEMENT_OPTIONS = {
  layout: "tabs",
  paymentMethodOrder: ["card", "paypal"],
  wallets: {
    applePay: "auto",
    googlePay: "auto",
    link: "never",
  },
};

const StripePaymentForm = ({ children, formattedAmount, onPaymentResult }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) {
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/next`,
        },
        redirect: "if_required",
      });

      if (error) {
        throw error;
      }
      await onPaymentResult(paymentIntent);
    } catch (error) {
      setErrorMessage(error?.message || t("paymentVerification.paymentFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const payLabel = formattedAmount
    ? t("paymentVerification.payAmount", { amount: formattedAmount })
    : t("paymentVerification.payNow");

  return (
    <form onSubmit={handleSubmit} className="contents">
      <div className="flow-step-scroll w-full">
        {children}

        <div className="mt-6 w-full text-left">
          <p className="mb-3 text-sm font-semibold text-flow-heading">
            {t("paymentVerification.paymentMethod")}
          </p>
          <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />
          <p className="mt-3 text-center text-xs text-flow-muted">
            {t("paymentVerification.secureHint")}
          </p>
        </div>

        {errorMessage && (
          <div
            className="mt-4 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700"
            role="alert"
            aria-live="assertive"
          >
            {errorMessage}
          </div>
        )}
      </div>

      <div className="flow-step-action-footer">
        <Button
          type="submit"
          variant="primaryWithoutIcon"
          disabled={!stripe || !elements || submitting}
        >
          <span className="me-1">{payLabel}</span>
          {submitting && <Preloader variant="white" />}
        </Button>
      </div>
    </form>
  );
};

export default StripePaymentForm;
